import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  getEstimateEasyPrzedmiar,
  getEstimateList,
  type EstimateEasyPrzedmiarRowDto,
  type EstimateFull,
  type EstimateHeader,
} from '../services/estimateService';
import EasyEstimatePrzedmiarTab, {
  filterEntriesToApprovedPozycjeOnly,
  filterPrzedmiarTable,
  parseEasyPrzedmiarApiRows,
  type EasyEstimatePrzedmiarColumnsHandle,
  type PrzedmiarPozycjaRow,
  type PrzedmiarTableEntry,
} from '../components/easyEstimate/EasyEstimatePrzedmiarTab';
import {
  makeEmptyScratchBundle,
  normalizeScratchGridProp,
  type CalcScratchBundle,
} from '../components/easyEstimate/PozycjaCalcScratchpad';
import {
  importVerifiedLibraryAth,
  listVerifiedLibrary,
  updateVerifiedLibraryPosition,
  bulkDeleteVerifiedLibrary,
  bulkPricePercentVerifiedLibrary,
  bulkSetRgVerifiedLibrary,
  bulkSetBranzaVerifiedLibrary,
  ALLOWED_BRANZE,
  BRANZA_LABELS,
  type LibraryBranza,
  type VerifiedLibPositionDto,
} from '../services/easyEstimateVerifiedLibraryService';

const PLACEHOLDER_AGGREGATE_ESTIMATE_ID = 0;
/** Prefix `lib:` pozwala rozróżnić pozycje z biblioteki ATH od pozycji agregowanych z kosztorysów. */
const LIBRARY_ENTRY_ID_PREFIX = 'lib:';

/** Wysokość ikony Material Symbols — spójna ze stylem paska narzędzi w kosztorysie EasyEstimate. */
const TOOLBAR_ICON_PX = 24;

/** Wspólny styl dla kwadratowych `IconButton` 32×32 w pasku akcji zbiorczych. */
const toolbarIconButtonSx = {
  width: 32,
  height: 32,
  borderRadius: '4px',
  p: 0,
  transition: 'background-color 0.15s ease',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
} as const;

/** Ikona Material Symbols Outlined (font ładowany globalnie w `public/index.html`). */
function MaterialSymbol({
  name,
  color = '#000000',
  fill = false,
}: {
  name: string;
  color?: string;
  fill?: boolean;
}) {
  return (
    <Box
      component="span"
      className="material-symbols-outlined"
      sx={{
        fontFamily: '"Material Symbols Outlined"',
        fontSize: TOOLBAR_ICON_PX,
        width: TOOLBAR_ICON_PX,
        height: TOOLBAR_ICON_PX,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        fontVariationSettings: `"FILL" ${fill ? 1 : 0}, "wght" 400, "GRAD" 0, "opsz" ${TOOLBAR_ICON_PX}`,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'geometricPrecision',
        userSelect: 'none',
      }}
      aria-hidden
    >
      {name}
    </Box>
  );
}

function makePlaceholderEstimate(rg?: number): EstimateFull {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: PLACEHOLDER_AGGREGATE_ESTIMATE_ID,
    positions: [],
    name: 'Wszystkie kosztorysy',
    object_name: 'Pozycje zweryfikowane',
    client: '',
    markup_kp: 0,
    markup_kz: 0,
    markup_z: 0,
    vat: 23,
    preparer: '—',
    prepared_at: today,
    updated_at: today,
    status: 'draft',
    ...(rg != null && Number.isFinite(rg) ? { rg } : {}),
  };
}

function estimateSectionLabel(h: EstimateHeader): string {
  const num = h.estimate_number?.trim();
  const title = (h.name || h.object_name || '').trim();
  if (num && title) return `${num} — ${title}`;
  if (num) return num;
  if (title) return title;
  return `Kosztorys #${h.id}`;
}

async function fetchPrzedmiarRowsForEstimate(estimateId: number): Promise<EstimateEasyPrzedmiarRowDto[]> {
  const data = await getEstimateEasyPrzedmiar(estimateId);
  if (!data.success || !data.rows?.length) return [];
  return data.rows;
}

/** Formatuje liczbę w formacie pl-PL z 2 miejscami (np. „125,50"). */
function formatMoneyPl(n: number): string {
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Konwertuje DTO biblioteki na wiersz pozycji + uzupełnia cellOverrides i calcScratchBundles. */
function hydrateLibraryPosition(
  dto: VerifiedLibPositionDto,
  entries: PrzedmiarTableEntry[],
  overrides: Record<string, string>,
  bundles: Record<string, CalcScratchBundle>,
  todayIso: string
): void {
  const id = `${LIBRARY_ENTRY_ID_PREFIX}${dto.id}`;
  const opisBase = String(dto.opis ?? dto.podstawa ?? '');
  const branzaLabel = dto.branza ? BRANZA_LABELS[dto.branza] ?? String(dto.branza) : '';
  const poz: PrzedmiarPozycjaRow = {
    kind: 'pozycja',
    id,
    lp: String(dto.source_ath_ordinal ?? dto.id),
    podstawa: String(dto.podstawa ?? ''),
    opis: opisBase,
    jm: String(dto.jm ?? ''),
    ilosc: String(dto.ilosc ?? '1'),
    cenaJedn: formatMoneyPl(Number(dto.cena_jedn) || 0),
    uwagi: String(dto.uwagi ?? ''),
    branza: branzaLabel,
    dzial: dto.source_ath_filename
      ? `Biblioteka (ATH): ${dto.source_ath_filename}`
      : 'Biblioteka (ATH)',
    approvedAt: todayIso,
    approvedByName: 'ATH',
  };
  entries.push(poz);

  const cena = Number(dto.cena_jedn);
  if (Number.isFinite(cena) && cena > 0) {
    overrides[`${id}:cenaJedn`] = formatMoneyPl(cena);
  }

  // Rekonstrukcja scratchpada z extras_json.calcScratchBundle (zapisanego w backendzie przy imporcie).
  const bundle = makeEmptyScratchBundle();
  const src = dto.extras?.calcScratchBundle;
  if (src && typeof src === 'object') {
    for (const sid of ['wlasne', 'knr', 'gemma'] as const) {
      const part = (src as Record<string, unknown>)[sid];
      if (part && typeof part === 'object') {
        const g = (part as { grid?: unknown }).grid;
        if (Array.isArray(g) && g.length) {
          bundle[sid].grid = normalizeScratchGridProp(g as string[][]);
        }
      }
    }
  }
  bundles[id] = bundle;
}

/**
 * Pozycje z `approvedAt` ze wszystkich kosztorysów (nieusuniętych) oraz pozycje z biblioteki ATH
 * (`easy_estimate_verified_library`) scalone w jedną tabelę. Strona dodatkowo wspiera import pliku ATH,
 * który trafia bezpośrednio do biblioteki.
 */
const EasyEstimateAllVerifiedPositionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<{
    entries: PrzedmiarTableEntry[];
    cellOverrides: Record<string, string>;
    calcScratchBundles: Record<string, CalcScratchBundle>;
    /** Mapa id biblioteki (number) → branża | null — potrzebne do filtrowania i selekcji. */
    libraryBranzaById: Map<number, LibraryBranza | null>;
    /** Lista id biblioteki w kolejności wyświetlania — do „zaznacz wszystkie". */
    libraryIds: number[];
    rg?: number;
  } | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [importBusy, setImportBusy] = React.useState(false);
  const [reloadTick, setReloadTick] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  /** Zaznaczone id pozycji z biblioteki (tylko lib:*). */
  const [selectedLibraryIds, setSelectedLibraryIds] = React.useState<Set<number>>(new Set());
  /** Filtr branża: '' = wszystkie; '__none__' = bez branży; lub konkretna branża. */
  const [branzaFilter, setBranzaFilter] = React.useState<'' | '__none__' | LibraryBranza>('');
  /** Wyszukiwanie tekstowe — sterowane z toolbar'a strony i przekazywane do `EasyEstimatePrzedmiarTab`. */
  const [search, setSearch] = React.useState('');
  const [bulkBusy, setBulkBusy] = React.useState(false);

  /** Uchwyt panelu „Dostosuj kolumny" rejestrowany przez `EasyEstimatePrzedmiarTab`. */
  const [columnsHandle, setColumnsHandle] = React.useState<EasyEstimatePrzedmiarColumnsHandle | null>(null);
  const handleColumnsPanelHandleReady = React.useCallback(
    (h: EasyEstimatePrzedmiarColumnsHandle | null) => setColumnsHandle(h),
    []
  );

  /** Dialog aktualnie otwarty (null = zamknięty).
   * `editRowBranza` — edycja branży dla pojedynczej pozycji biblioteki (z menu wiersza). */
  const [openDialog, setOpenDialog] = React.useState<
    | null
    | { kind: 'pricePercent' }
    | { kind: 'setRg' }
    | { kind: 'setBranza' }
    | { kind: 'confirmDelete' }
    | { kind: 'editRowBranza'; libId: number }
  >(null);
  const [priceDeltaInput, setPriceDeltaInput] = React.useState('10');
  const [rgInput, setRgInput] = React.useState('40');
  const [branzaInput, setBranzaInput] = React.useState<'' | LibraryBranza>('');
  /** Draft edycji branży pojedynczego wiersza (dialog otwierany z menu „⋮"). */
  const [editRowBranzaInput, setEditRowBranzaInput] = React.useState<'' | LibraryBranza>('');
  const [editRowBranzaBusy, setEditRowBranzaBusy] = React.useState(false);

  const todayIso = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [list, library] = await Promise.all([
          getEstimateList(),
          listVerifiedLibrary(),
        ]);
        if (cancelled) return;
        const active = list.filter((h) => h.status !== 'deleted');
        const chunkSize = 8;
        const mergedEntries: PrzedmiarTableEntry[] = [];
        const mergedOverrides: Record<string, string> = {};
        const mergedScratch: Record<string, CalcScratchBundle> = {};
        const libraryBranzaById = new Map<number, LibraryBranza | null>();
        const libraryIds: number[] = [];
        let rgFallback: number | undefined;

        type Strip = {
          header: EstimateHeader;
          pozycje: PrzedmiarPozycjaRow[];
          cellOverrides: Record<string, string>;
          calcScratchBundles: Record<string, CalcScratchBundle>;
        };
        const strips: Strip[] = [];

        for (let i = 0; i < active.length; i += chunkSize) {
          const chunk = active.slice(i, i + chunkSize);
          const rowsPerEst = await Promise.all(chunk.map((h) => fetchPrzedmiarRowsForEstimate(h.id)));
          if (cancelled) return;

          for (let j = 0; j < chunk.length; j++) {
            const h = chunk[j];
            const rows = rowsPerEst[j];
            if (!rows.length) continue;
            const parsed = parseEasyPrzedmiarApiRows(rows);
            const base = filterPrzedmiarTable(parsed.entries, 'wszystkie', '', false);
            const approvedOnly = filterEntriesToApprovedPozycjeOnly(base);
            const pozycje = approvedOnly.filter((e): e is PrzedmiarPozycjaRow => e.kind === 'pozycja');
            if (pozycje.length === 0) continue;
            if (rgFallback == null && h.rg != null && Number.isFinite(h.rg)) rgFallback = h.rg;

            strips.push({
              header: h,
              pozycje,
              cellOverrides: parsed.cellOverrides,
              calcScratchBundles: parsed.calcScratchBundles,
            });
          }
        }

        // 1) Pozycje z biblioteki ATH na górze (najnowsze batche pierwsze — API zwraca po batch_id/ordinal)
        for (const dto of library) {
          hydrateLibraryPosition(dto, mergedEntries, mergedOverrides, mergedScratch, todayIso);
          libraryBranzaById.set(dto.id, dto.branza ?? null);
          libraryIds.push(dto.id);
        }

        // 2) Pozycje zatwierdzone z kosztorysów (agregat)
        const multiKosztorysy = strips.length > 1;
        for (const strip of strips) {
          const label = estimateSectionLabel(strip.header);
          Object.assign(mergedOverrides, strip.cellOverrides);
          Object.assign(mergedScratch, strip.calcScratchBundles);
          for (const p of strip.pozycje) {
            mergedEntries.push(
              multiKosztorysy ? { ...p, opis: `[${label}] ${p.opis}` } : { ...p }
            );
          }
        }

        if (cancelled) return;
        setSnapshot({
          entries: mergedEntries,
          cellOverrides: mergedOverrides,
          calcScratchBundles: mergedScratch,
          libraryBranzaById,
          libraryIds,
          rg: rgFallback,
        });
        /* Usuń z selekcji te ID, które już nie istnieją. */
        setSelectedLibraryIds((prev) => {
          const keep = new Set<number>();
          Array.from(prev).forEach((id) => {
            if (libraryBranzaById.has(id)) keep.add(id);
          });
          return keep;
        });
      } catch (e: unknown) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Nie udało się wczytać danych.');
          setSnapshot(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadTick, todayIso]);

  const placeholder = makePlaceholderEstimate(snapshot?.rg);

  /** Zbiór id (w formacie `lib:<n>`) które zostały przefiltrowane przez branżę. */
  const filteredOutLibEntryIds = React.useMemo(() => {
    const out = new Set<string>();
    if (!snapshot || branzaFilter === '') return out;
    Array.from(snapshot.libraryBranzaById.entries()).forEach(([libId, b]) => {
      const keep = branzaFilter === '__none__' ? b == null : b === branzaFilter;
      if (!keep) out.add(`${LIBRARY_ENTRY_ID_PREFIX}${libId}`);
    });
    return out;
  }, [snapshot, branzaFilter]);

  const externalPrzedmiarSnapshot = React.useMemo(() => {
    if (!snapshot || snapshot.entries.length === 0) return null;
    /* Gdy mamy aktywny filtr branży — odfiltrowujemy pozycje z biblioteki nie pasujące do branży.
     * Pozycje zagregowane z kosztorysów są zachowane (branża nie dotyczy ich). */
    const entries = filteredOutLibEntryIds.size === 0
      ? snapshot.entries
      : snapshot.entries.filter((e) => !(e.kind === 'pozycja' && filteredOutLibEntryIds.has(e.id)));
    return {
      entries,
      cellOverrides: snapshot.cellOverrides,
      calcScratchBundles: snapshot.calcScratchBundles,
    };
  }, [snapshot, filteredOutLibEntryIds]);

  /** Lista id biblioteki po zastosowaniu filtra branży (do selekcji „zaznacz wszystkie widoczne"). */
  const visibleLibraryIds = React.useMemo(() => {
    if (!snapshot) return [];
    if (branzaFilter === '') return snapshot.libraryIds;
    return snapshot.libraryIds.filter((libId) => {
      const b = snapshot.libraryBranzaById.get(libId) ?? null;
      return branzaFilter === '__none__' ? b == null : b === branzaFilter;
    });
  }, [snapshot, branzaFilter]);

  const allVisibleSelected =
    visibleLibraryIds.length > 0 && visibleLibraryIds.every((id) => selectedLibraryIds.has(id));

  const handleToggleSelectAll = React.useCallback(() => {
    if (allVisibleSelected) {
      setSelectedLibraryIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleLibraryIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedLibraryIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleLibraryIds) next.add(id);
        return next;
      });
    }
  }, [allVisibleSelected, visibleLibraryIds]);

  const selectedCount = selectedLibraryIds.size;
  const selectedIdsArray = React.useMemo(() => Array.from(selectedLibraryIds), [selectedLibraryIds]);

  const handleUploadClick = React.useCallback(() => {
    if (importBusy) return;
    fileInputRef.current?.click();
  }, [importBusy]);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      event.target.value = '';
      if (!file) return;
      if (!/\.ath$/i.test(file.name)) {
        setSnackbar({ open: true, message: 'Wybierz plik .ath (Athenasoft).' });
        return;
      }
      setImportBusy(true);
      try {
        const result = await importVerifiedLibraryAth(file);
        setSnackbar({
          open: true,
          message: `Zaimportowano ${result.imported_positions} pozycji z pliku ${result.filename ?? file.name}.`,
        });
        setReloadTick((n) => n + 1);
      } catch (err) {
        console.error('Import ATH (biblioteka) error:', err);
        setSnackbar({ open: true, message: 'Nie udało się zaimportować pliku ATH.' });
      } finally {
        setImportBusy(false);
      }
    },
    []
  );

  /** Persystuje zmiany scratchpada dla pozycji z biblioteki (`id` rozpoczynający się od `lib:`). */
  const handleExternalScratchBundleChange = React.useCallback(
    (entryId: string, bundle: CalcScratchBundle) => {
      if (!entryId.startsWith(LIBRARY_ENTRY_ID_PREFIX)) return;
      const libId = Number(entryId.slice(LIBRARY_ENTRY_ID_PREFIX.length));
      if (!Number.isFinite(libId)) return;
      /* Debounce per-id: prosty setTimeout — UI jest szybki, więc ostatni zapis wygrywa. */
      const key = `libScratchSave:${libId}`;
      const existingTimer = window[key as keyof Window] as unknown as number | undefined;
      if (typeof existingTimer === 'number') window.clearTimeout(existingTimer);
      const timerId = window.setTimeout(async () => {
        try {
          await updateVerifiedLibraryPosition(libId, {
            extras: {
              calcScratchBundle: bundle,
            },
          });
        } catch (err) {
          console.error('Zapis scratchpada biblioteki nieudany', err);
        }
      }, 600);
      (window as unknown as Record<string, number>)[key] = timerId as unknown as number;
    },
    []
  );

  /** Persystuje wstawienie cena jedn. dla pozycji z biblioteki. Parsuje liczbę z formatu pl-PL. */
  const handleExternalCenaJednChange = React.useCallback(
    (entryId: string, formatted: string) => {
      if (!entryId.startsWith(LIBRARY_ENTRY_ID_PREFIX)) return;
      const libId = Number(entryId.slice(LIBRARY_ENTRY_ID_PREFIX.length));
      if (!Number.isFinite(libId)) return;
      const numeric = parseFloat(String(formatted).replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(numeric)) return;
      void (async () => {
        try {
          await updateVerifiedLibraryPosition(libId, { cena_jedn: numeric });
        } catch (err) {
          console.error('Zapis cena_jedn biblioteki nieudany', err);
        }
      })();
    },
    []
  );

  /* ================ Akcje zbiorcze ================ */

  const runBulk = React.useCallback(
    async (
      label: string,
      fn: () => Promise<{ deleted?: number; updated?: number }>
    ) => {
      if (bulkBusy) return;
      setBulkBusy(true);
      try {
        const res = await fn();
        const count = res.deleted ?? res.updated ?? 0;
        setSnackbar({ open: true, message: `${label}: ${count} pozycji.` });
        setSelectedLibraryIds(new Set());
        setOpenDialog(null);
        setReloadTick((n) => n + 1);
      } catch (err) {
        console.error('Bulk akcja nieudana:', err);
        setSnackbar({ open: true, message: `Akcja zbiorcza „${label}" nie powiodła się.` });
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkBusy]
  );

  const handleConfirmDelete = React.useCallback(() => {
    void runBulk('Usunięto', () => bulkDeleteVerifiedLibrary(selectedIdsArray));
  }, [runBulk, selectedIdsArray]);

  const handleConfirmPricePercent = React.useCallback(() => {
    const pct = parseFloat(priceDeltaInput.replace(',', '.'));
    if (!Number.isFinite(pct)) {
      setSnackbar({ open: true, message: 'Niepoprawny procent.' });
      return;
    }
    void runBulk(`Zmieniono cenę o ${pct >= 0 ? '+' : ''}${pct}%`, () =>
      bulkPricePercentVerifiedLibrary(selectedIdsArray, pct)
    );
  }, [runBulk, selectedIdsArray, priceDeltaInput]);

  const handleConfirmSetRg = React.useCallback(() => {
    const rate = parseFloat(rgInput.replace(',', '.'));
    if (!Number.isFinite(rate) || rate < 0) {
      setSnackbar({ open: true, message: 'Niepoprawna stawka RG.' });
      return;
    }
    void runBulk(`Ustawiono stawkę RG = ${rate} zł`, () =>
      bulkSetRgVerifiedLibrary(selectedIdsArray, rate)
    );
  }, [runBulk, selectedIdsArray, rgInput]);

  const handleConfirmSetBranza = React.useCallback(() => {
    const b = branzaInput === '' ? null : (branzaInput as LibraryBranza);
    void runBulk(`Ustawiono branżę ${b ? `„${BRANZA_LABELS[b]}"` : '(pusta)'}`, () =>
      bulkSetBranzaVerifiedLibrary(selectedIdsArray, b)
    );
  }, [runBulk, selectedIdsArray, branzaInput]);

  /** Otwiera dialog edycji branży dla pojedynczej pozycji z biblioteki (wyzwalane z menu wiersza). */
  const handleOpenEditRowBranza = React.useCallback(
    (entryId: string) => {
      if (!entryId.startsWith(LIBRARY_ENTRY_ID_PREFIX)) return;
      const libId = Number(entryId.slice(LIBRARY_ENTRY_ID_PREFIX.length));
      if (!Number.isFinite(libId)) return;
      const current = snapshot?.libraryBranzaById.get(libId) ?? null;
      setEditRowBranzaInput(current ?? '');
      setOpenDialog({ kind: 'editRowBranza', libId });
    },
    [snapshot]
  );

  /** Zapis branży pojedynczej pozycji i przeładowanie snapshotu. */
  const handleConfirmEditRowBranza = React.useCallback(async () => {
    if (openDialog?.kind !== 'editRowBranza') return;
    const libId = openDialog.libId;
    const b = editRowBranzaInput === '' ? null : (editRowBranzaInput as LibraryBranza);
    setEditRowBranzaBusy(true);
    try {
      await updateVerifiedLibraryPosition(libId, { branza: b });
      setSnackbar({
        open: true,
        message: `Zaktualizowano branżę${b ? `: „${BRANZA_LABELS[b]}"` : ' (wyczyszczono)'}.`,
      });
      setOpenDialog(null);
      setReloadTick((n) => n + 1);
    } catch (err) {
      console.error('Zapis branży pozycji nieudany', err);
      setSnackbar({ open: true, message: 'Nie udało się zapisać branży pozycji.' });
    } finally {
      setEditRowBranzaBusy(false);
    }
  }, [openDialog, editRowBranzaInput]);

  if (loading) {
    return (
      <Box sx={{ p: 2, flex: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#5F6368' }}>
        Ładowanie pozycji zweryfikowanych…
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2, flex: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#c62828' }}>
        {loadError}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e8eaed',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <IconButton
            size="small"
            aria-label="Wróć do EasyEstimate"
            onClick={() => navigate('/app/easy-estimate')}
            sx={{ color: '#020242' }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '18px', fontWeight: 500, color: '#020242' }}>
            Pozycje zweryfikowane (wszystkie kosztorysy)
          </Typography>
        </Box>
        <Tooltip title={importBusy ? 'Trwa import…' : 'Wczytaj kosztorys ATH do biblioteki pozycji zweryfikowanych'}>
          <span>
            <IconButton
              size="medium"
              aria-label="Wczytaj kosztorys ATH"
              disabled={importBusy}
              onClick={handleUploadClick}
              sx={{ color: '#5F6368' }}
            >
              <Box
                component="img"
                src="/assets/icons/upload_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                alt="Wczytaj kosztorys"
                sx={{ width: 24, height: 24, opacity: importBusy ? 0.5 : 1 }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept=".ath"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      {/* Toolbar w jednym wierszu — styl spójny z paskiem narzędzi w kosztorysie (IconButton 32×32 + Material Symbols). */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FFFFFF',
          minHeight: 40,
          overflowX: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
        <Tooltip
          title={
            allVisibleSelected
              ? `Odznacz wszystkie widoczne pozycje biblioteki (${visibleLibraryIds.length})`
              : `Zaznacz wszystkie widoczne pozycje biblioteki (${visibleLibraryIds.length})`
          }
        >
          <span>
            <IconButton
              size="small"
              aria-label={
                allVisibleSelected ? 'Odznacz wszystkie pozycje biblioteki' : 'Zaznacz wszystkie pozycje biblioteki'
              }
              disabled={visibleLibraryIds.length === 0 || bulkBusy}
              onClick={handleToggleSelectAll}
              sx={{
                ...toolbarIconButtonSx,
                bgcolor: allVisibleSelected ? '#E0E0F5' : 'transparent',
              }}
            >
              <MaterialSymbol
                name={allVisibleSelected ? 'check_box' : 'select_all'}
                fill={allVisibleSelected}
              />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          sx={{
            fontSize: 13,
            color: '#5F6368',
            fontFamily: 'Roboto, sans-serif',
            whiteSpace: 'nowrap',
            ml: 0.25,
            mr: 0.5,
          }}
        >
          Zaznaczono: <strong>{selectedCount}</strong> / {visibleLibraryIds.length}
        </Typography>

        <Box sx={{ width: 1, height: 24, bgcolor: '#e8eaed', mx: 0.25 }} />

        <Tooltip title="Usuń zaznaczone pozycje z biblioteki">
          <span>
            <IconButton
              size="small"
              aria-label="Usuń zaznaczone pozycje"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => setOpenDialog({ kind: 'confirmDelete' })}
              sx={toolbarIconButtonSx}
            >
              <MaterialSymbol name="delete" color={selectedCount === 0 ? '#9AA0A6' : '#B00020'} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Zwiększ/zmniejsz cenę jedn. o procent dla zaznaczonych">
          <span>
            <IconButton
              size="small"
              aria-label="Zmień cenę jednostkową o procent"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => setOpenDialog({ kind: 'pricePercent' })}
              sx={toolbarIconButtonSx}
            >
              <MaterialSymbol name="percent" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Wstaw stawkę roboczogodziny (zł/rg) — przelicza cenę jedn. dla zaznaczonych">
          <span>
            <IconButton
              size="small"
              aria-label="Ustaw stawkę roboczogodziny"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => setOpenDialog({ kind: 'setRg' })}
              sx={toolbarIconButtonSx}
            >
              <MaterialSymbol name="handyman" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Ustaw branżę dla zaznaczonych pozycji">
          <span>
            <IconButton
              size="small"
              aria-label="Ustaw branżę dla zaznaczonych"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => {
                setBranzaInput('');
                setOpenDialog({ kind: 'setBranza' });
              }}
              sx={toolbarIconButtonSx}
            >
              <MaterialSymbol name="sell" />
            </IconButton>
          </span>
        </Tooltip>

        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', flexShrink: 0 }}>
        <Tooltip title="Dostosuj kolumny — widoczność, kolejność i szerokość">
          <span>
            <IconButton
              size="small"
              aria-label="Dostosuj kolumny"
              disabled={!columnsHandle}
              onClick={() => columnsHandle?.open()}
              sx={{
                ...toolbarIconButtonSx,
                bgcolor: columnsHandle?.isOpen ? '#E0E0F5' : 'transparent',
              }}
            >
              <Box
                component="img"
                alt="kolumny"
                src="/assets/icons/frame-344.svg?v=2"
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Szukaj"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: 240,
            fontFamily: 'Roboto, sans-serif',
            '& .MuiOutlinedInput-root': { height: 32, fontSize: 14 },
            '& .MuiOutlinedInput-input': { py: 0 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#595966', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{ fontSize: 13, color: '#5F6368', fontFamily: 'Roboto, sans-serif', whiteSpace: 'nowrap' }}
          >
            Branża:
          </Typography>
          <Select
            size="small"
            value={branzaFilter}
            onChange={(e) => setBranzaFilter(e.target.value as typeof branzaFilter)}
            sx={{
              minWidth: 160,
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              height: 32,
              '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center', minHeight: 'unset' },
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            <MenuItem value="__none__"><em>Bez branży</em></MenuItem>
            {ALLOWED_BRANZE.map((b) => (
              <MenuItem key={b} value={b}>
                {BRANZA_LABELS[b]}
              </MenuItem>
            ))}
          </Select>
        </Box>
        </Box>
      </Box>

      {!externalPrzedmiarSnapshot ? (
        <Box sx={{ p: 2, color: '#5F6368', fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>
          Brak pozycji zweryfikowanych. Użyj przycisku „Wczytaj kosztorys ATH" powyżej, aby zasilić bibliotekę
          z pliku Athenasoft, albo zatwierdź pozycje w istniejących kosztorysach.
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <EasyEstimatePrzedmiarTab
            estimate={placeholder}
            kosztorysTabActive={false}
            roboczogodzinaZl={placeholder.rg != null ? String(placeholder.rg) : ''}
            layoutVariant="verifiedList"
            externalPrzedmiarSnapshot={externalPrzedmiarSnapshot}
            onExternalScratchBundleChange={handleExternalScratchBundleChange}
            onExternalCenaJednChange={handleExternalCenaJednChange}
            externalSearch={search}
            onExternalSearchChange={setSearch}
            onColumnsPanelHandleReady={handleColumnsPanelHandleReady}
            onMenuEditBranza={handleOpenEditRowBranza}
          />
        </Box>
      )}

      {/* ===== Dialogi akcji zbiorczych ===== */}
      <Dialog open={openDialog?.kind === 'confirmDelete'} onClose={() => setOpenDialog(null)}>
        <DialogTitle>Usuń zaznaczone pozycje?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>
            Zostanie usuniętych <strong>{selectedCount}</strong> pozycji z biblioteki zweryfikowanych.
            Tej operacji nie da się cofnąć.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={bulkBusy}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={bulkBusy}>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog?.kind === 'pricePercent'} onClose={() => setOpenDialog(null)}>
        <DialogTitle>Zmień cenę jedn. o procent</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#5F6368', mb: 1 }}>
            Procent dodatni = podwyżka, ujemny = obniżka. Przykład: 10 = +10 %, -5 = -5 %.
          </Typography>
          <TextField
            autoFocus
            label="Procent (%)"
            size="small"
            fullWidth
            value={priceDeltaInput}
            onChange={(e) => setPriceDeltaInput(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={bulkBusy}>Anuluj</Button>
          <Button onClick={handleConfirmPricePercent} variant="contained" disabled={bulkBusy}>
            Zastosuj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog?.kind === 'setRg'} onClose={() => setOpenDialog(null)}>
        <DialogTitle>Wstaw stawkę roboczogodziny</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#5F6368', mb: 1 }}>
            Stawka zostanie ustawiona w kolumnie D (cena jedn.) dla wszystkich wierszy typu R
            w zakładce KNR, a cena jedn. pozycji zostanie przeliczona z uwzględnieniem narzutów.
          </Typography>
          <TextField
            autoFocus
            label="Stawka zł/rg"
            size="small"
            fullWidth
            value={rgInput}
            onChange={(e) => setRgInput(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={bulkBusy}>Anuluj</Button>
          <Button onClick={handleConfirmSetRg} variant="contained" disabled={bulkBusy}>
            Zastosuj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog?.kind === 'setBranza'} onClose={() => setOpenDialog(null)}>
        <DialogTitle>Ustaw branżę dla zaznaczonych</DialogTitle>
        <DialogContent>
          <Select
            value={branzaInput}
            onChange={(e) => setBranzaInput(e.target.value as '' | LibraryBranza)}
            size="small"
            fullWidth
            displayEmpty
          >
            <MenuItem value=""><em>Usuń branżę (pusta)</em></MenuItem>
            {ALLOWED_BRANZE.map((b) => (
              <MenuItem key={b} value={b}>
                {BRANZA_LABELS[b]}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={bulkBusy}>Anuluj</Button>
          <Button onClick={handleConfirmSetBranza} variant="contained" disabled={bulkBusy}>
            Zastosuj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog?.kind === 'editRowBranza'}
        onClose={() => (editRowBranzaBusy ? undefined : setOpenDialog(null))}
      >
        <DialogTitle>Edytuj branżę pozycji</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: '#5F6368', mb: 1 }}>
            Wybierz branżę z listy lub „Brak" aby wyczyścić.
          </Typography>
          <Select
            value={editRowBranzaInput}
            onChange={(e) => setEditRowBranzaInput(e.target.value as '' | LibraryBranza)}
            size="small"
            fullWidth
            displayEmpty
            autoFocus
          >
            <MenuItem value=""><em>Brak (wyczyść)</em></MenuItem>
            {ALLOWED_BRANZE.map((b) => (
              <MenuItem key={b} value={b}>
                {BRANZA_LABELS[b]}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={editRowBranzaBusy}>Anuluj</Button>
          <Button
            onClick={() => void handleConfirmEditRowBranza()}
            variant="contained"
            disabled={editRowBranzaBusy}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default EasyEstimateAllVerifiedPositionsPage;
